import { createContext, useContext, useState, ReactNode } from "react";

// ── Types ─────────────────────────────────────────────────
export type MealOption = { name: string; calories: number; protein: number; fat: number };
export type WeekPlan   = { breakfast: MealOption[]; lunch: MealOption[]; snack: MealOption[]; dinner: MealOption[] };
export type MealKey    = "breakfast" | "lunch" | "snack" | "dinner";
export type Selections = Record<MealKey, number>;

// ── Shared data ───────────────────────────────────────────
export const WEEKS = ["Week 1", "Week 2", "Week 3", "Week 4"];

export const MEAL_META: { key: MealKey; label: string; emoji: string }[] = [
  { key: "breakfast", label: "Breakfast", emoji: "🥣" },
  { key: "lunch",     label: "Lunch",     emoji: "🍗" },
  { key: "snack",     label: "Snack",     emoji: "🍎" },
  { key: "dinner",    label: "Dinner",    emoji: "🥗" },
];

export const DIET_PLANS: Record<string, WeekPlan> = {
  "Week 1": {
    breakfast: [
      { name: "Oats + Banana",               calories: 320, protein: 12, fat:  6 },
      { name: "Eggs + Whole Wheat Toast",     calories: 380, protein: 22, fat: 14 },
      { name: "Greek Yogurt + Berries",       calories: 290, protein: 18, fat:  5 },
    ],
    lunch: [
      { name: "Grilled Chicken + Rice",       calories: 520, protein: 42, fat: 10 },
      { name: "Turkey Wrap + Side Salad",     calories: 490, protein: 38, fat: 12 },
      { name: "Salmon + Quinoa",              calories: 540, protein: 44, fat: 14 },
    ],
    snack: [
      { name: "Apple + Peanut Butter",        calories: 180, protein:  5, fat:  9 },
      { name: "Protein Bar",                  calories: 210, protein: 18, fat:  7 },
      { name: "Mixed Nuts",                   calories: 160, protein:  4, fat: 14 },
    ],
    dinner: [
      { name: "Tuna Salad + Brown Toast",     calories: 280, protein: 30, fat:  8 },
      { name: "Grilled Fish + Vegetables",    calories: 340, protein: 38, fat: 12 },
      { name: "Chicken Soup + Bread",         calories: 300, protein: 28, fat:  7 },
    ],
  },
  "Week 2": {
    breakfast: [
      { name: "Smoothie Bowl + Nuts",         calories: 350, protein: 14, fat: 18 },
      { name: "Avocado Toast + Poached Egg",  calories: 380, protein: 16, fat: 22 },
      { name: "Omelette + Veggies",           calories: 340, protein: 24, fat: 18 },
    ],
    lunch: [
      { name: "Beef + Sweet Potato",          calories: 560, protein: 40, fat: 18 },
      { name: "Lentil Soup + Rice",           calories: 480, protein: 22, fat:  6 },
      { name: "Grilled Chicken Caesar Wrap",  calories: 500, protein: 40, fat: 14 },
    ],
    snack: [
      { name: "Banana + Almonds",             calories: 190, protein:  4, fat:  9 },
      { name: "Cottage Cheese + Cucumber",    calories: 150, protein: 16, fat:  4 },
      { name: "Rice Cakes + Hummus",          calories: 170, protein:  5, fat:  6 },
    ],
    dinner: [
      { name: "Beef Stir-fry + Brown Rice",   calories: 420, protein: 35, fat: 16 },
      { name: "Whole Wheat Pasta + Tomato",   calories: 420, protein: 16, fat:  8 },
      { name: "Tuna Wrap + Greens",           calories: 390, protein: 36, fat: 12 },
    ],
  },
  "Week 3": {
    breakfast: [
      { name: "Protein Shake + Fruit Bowl",   calories: 310, protein: 28, fat:  6 },
      { name: "Cereal + Skim Milk",           calories: 300, protein: 10, fat:  5 },
      { name: "Pancakes + Honey",             calories: 410, protein: 12, fat: 10 },
    ],
    lunch: [
      { name: "Shrimp Stir-fry + Rice",       calories: 490, protein: 38, fat: 10 },
      { name: "Tuna Salad Sandwich",          calories: 460, protein: 36, fat: 14 },
      { name: "Grilled Salmon + Greens",      calories: 520, protein: 44, fat: 18 },
    ],
    snack: [
      { name: "Greek Yogurt + Honey",         calories: 160, protein: 12, fat:  4 },
      { name: "Boiled Eggs × 2",              calories: 140, protein: 12, fat: 10 },
      { name: "Fresh Fruit Salad",            calories: 120, protein:  2, fat:  1 },
    ],
    dinner: [
      { name: "Grilled Chicken + Salad",      calories: 350, protein: 40, fat: 10 },
      { name: "Lentil Soup + Whole Grain",    calories: 320, protein: 14, fat:  9 },
      { name: "Stuffed Bell Peppers",         calories: 380, protein: 26, fat: 12 },
    ],
  },
  "Week 4": {
    breakfast: [
      { name: "French Toast + Berries",       calories: 380, protein: 14, fat: 12 },
      { name: "Smoothie Bowl + Chia",         calories: 320, protein: 10, fat:  8 },
      { name: "Eggs + Avocado",               calories: 390, protein: 20, fat: 26 },
    ],
    lunch: [
      { name: "Roast Chicken + Veggies",      calories: 510, protein: 44, fat: 16 },
      { name: "Lean Grilled Burger",          calories: 540, protein: 36, fat: 22 },
      { name: "Chickpea Salad Bowl",          calories: 450, protein: 20, fat: 14 },
    ],
    snack: [
      { name: "Dates + Walnuts",              calories: 200, protein:  3, fat: 10 },
      { name: "Whey Protein Shake",           calories: 170, protein: 24, fat:  3 },
      { name: "Edamame",                      calories: 130, protein: 11, fat:  5 },
    ],
    dinner: [
      { name: "Grilled Steak + Green Salad",  calories: 480, protein: 45, fat: 22 },
      { name: "Sushi Platter + Miso Soup",    calories: 420, protein: 28, fat: 10 },
      { name: "Grilled Veggies + Eggs",       calories: 300, protein: 22, fat: 14 },
    ],
  },
};

export const DEFAULT_SELECTIONS: Selections = { breakfast: 0, lunch: 0, snack: 0, dinner: 0 };

// Weeks the dietitian has unlocked for this patient.
// In production this comes from the API (e.g. GET /patient/plan/unlocked-weeks).
export const UNLOCKED_WEEKS: string[] = ["Week 1"];

// ── Context ───────────────────────────────────────────────
type DietContextType = {
  selectedWeek: string;
  selections: Selections;
  unlockedWeeks: string[];
  setSelectedWeek: (week: string) => void;
  setSelections: (s: Selections | ((prev: Selections) => Selections)) => void;
};

const DietContext = createContext<DietContextType | null>(null);

export const DietProvider = ({ children }: { children: ReactNode }) => {
  const [selectedWeek, setSelectedWeek] = useState("Week 1");
  const [selections, setSelections]     = useState<Selections>(DEFAULT_SELECTIONS);
  // Replace with API fetch in production
  const [unlockedWeeks]                 = useState<string[]>(UNLOCKED_WEEKS);

  return (
    <DietContext.Provider value={{ selectedWeek, selections, unlockedWeeks, setSelectedWeek, setSelections }}>
      {children}
    </DietContext.Provider>
  );
};

export const useDiet = () => {
  const ctx = useContext(DietContext);
  if (!ctx) throw new Error("useDiet must be used within DietProvider");
  return ctx;
};
